import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import CipOpDonut from "./CipOpDonut";

function Stat({ label, value, testid }) {
  return (
    <div className="min-w-[72px]">
      <div data-testid={testid} className="font-display font-black text-3xl tracking-tighter tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}

// Self-contained "Library coverage counts" charts (full NERC set vs. the entity's
// applicable subset, with CIP-vs-O&P donuts). Reused on the Home dashboard and the
// Standards Library. Fetches its own data so callers just drop it in.
export default function StandardsCoverageSummary() {
  const [reqs, setReqs] = useState([]);
  const [regFuncs, setRegFuncs] = useState([]);
  const [ready, setReady] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/requirements"), api.get("/entity-profile")])
      .then(([r, p]) => { setReqs(r.data || []); setRegFuncs(p.data?.registered_functions || []); })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const hasProfile = regFuncs.length > 0;
  const appliesToEntity = (r) => hasProfile && r.applicable === true;

  const counts = useMemo(() => {
    const subOf = (r) => (r.sub_requirements || []).length;
    const isCip = (sid) => (sid || "").toUpperCase().startsWith("CIP-");
    const bucket = (r) => (isCip(r.standard_id) ? "cip" : "op");
    const isEnforceableNow = (r) => ((r.access?.status || r.status) === "currently_enforceable");
    const emptySplit = () => ({ standards: new Set(), reqs: 0, subs: 0 });
    const finalizeSplit = (s) => ({ standards: s.standards.size, reqs: s.reqs, subs: s.subs });
    const addToSplit = (s, r) => { s.standards.add(r.standard_id); s.reqs += 1; s.subs += subOf(r); };

    const enfReqs = reqs.filter(isEnforceableNow);
    const totalCip = emptySplit(), totalOp = emptySplit();
    for (const r of enfReqs) addToSplit(bucket(r) === "cip" ? totalCip : totalOp, r);
    const enfStandardIds = new Set(enfReqs.map((r) => r.standard_id));
    const total = {
      standards: enfStandardIds.size,
      reqs: enfReqs.length,
      subs: enfReqs.reduce((a, r) => a + subOf(r), 0),
      cip: finalizeSplit(totalCip),
      op: finalizeSplit(totalOp),
      future: reqs.filter((r) => (r.access?.status || r.status) === "future_enforceable").length,
      retired: reqs.filter((r) => (r.access?.status || r.status) === "retired").length,
    };

    const appReqs = hasProfile ? enfReqs.filter(appliesToEntity) : [];
    const appCip = emptySplit(), appOp = emptySplit();
    for (const r of appReqs) addToSplit(bucket(r) === "cip" ? appCip : appOp, r);
    const applicable = {
      standards: new Set(appReqs.map((r) => r.standard_id)).size,
      reqs: appReqs.length,
      subs: appReqs.reduce((a, r) => a + subOf(r), 0),
      cip: finalizeSplit(appCip),
      op: finalizeSplit(appOp),
      future: hasProfile ? reqs.filter((r) => (r.access?.status || r.status) === "future_enforceable" && appliesToEntity(r)).length : 0,
    };
    return { total, applicable };
  }, [reqs, regFuncs, hasProfile]);

  if (!ready) return null;

  return (
    <div data-testid="requirements-count-summary" className="grid sm:grid-cols-2 gap-px bg-border border border-border">
      <div className="bg-card p-5">
        <div className="overline text-muted-foreground mb-3">Across all NERC standards <span className="text-[10px] normal-case font-normal text-muted-foreground/70">(currently enforceable)</span></div>
        <div className="flex gap-8">
          <Stat label="Standards" value={counts.total.standards} testid="count-total-standards" />
          <Stat label="Requirements" value={counts.total.reqs} testid="count-total-requirements" />
          <Stat label="Parts/Subparts" value={counts.total.subs} testid="count-total-subparts" />
        </div>
        {(counts.total.future > 0 || counts.total.retired > 0) && (
          <div className="mt-1 text-[11px] text-muted-foreground" data-testid="count-total-nonactive">
            {counts.total.future > 0 && <span>+{counts.total.future} future-enforceable</span>}
            {counts.total.future > 0 && counts.total.retired > 0 && <span> · </span>}
            {counts.total.retired > 0 && <span>{counts.total.retired} retired</span>}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">CIP vs O&amp;P split</div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <CipOpDonut cip={counts.total.cip.reqs + counts.total.cip.subs} op={counts.total.op.reqs + counts.total.op.subs} size={112} label="reqs + subs" testid="donut-total" />
            <div className="text-xs font-mono text-muted-foreground space-y-0.5">
              <div>CIP: {counts.total.cip.standards} standards · {counts.total.cip.reqs} reqs · {counts.total.cip.subs} subs</div>
              <div>O&amp;P: {counts.total.op.standards} standards · {counts.total.op.reqs} reqs · {counts.total.op.subs} subs</div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card p-5">
        <div className="overline text-muted-foreground mb-3">
          Applicable to your registration <span className="text-[10px] normal-case font-normal text-muted-foreground/70">(currently enforceable)</span>
          {hasProfile && <span className="font-mono text-foreground/70 ml-2 normal-case">{regFuncs.join(", ")}</span>}
        </div>
        {hasProfile ? (
          <>
            <div className="flex gap-8">
              <Stat label="Standards" value={counts.applicable.standards} testid="count-applicable-standards" />
              <Stat label="Requirements" value={counts.applicable.reqs} testid="count-applicable-requirements" />
              <Stat label="Parts/Subparts" value={counts.applicable.subs} testid="count-applicable-subparts" />
            </div>
            {counts.applicable.future > 0 && (
              <div className="mt-1 text-[11px] text-muted-foreground" data-testid="count-applicable-future">
                +{counts.applicable.future} future-enforceable applicable to your registration
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">CIP vs O&amp;P split</div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <CipOpDonut cip={counts.applicable.cip.reqs + counts.applicable.cip.subs} op={counts.applicable.op.reqs + counts.applicable.op.subs} size={112} label="reqs + subs" testid="donut-applicable" />
                <div className="text-xs font-mono text-muted-foreground space-y-0.5">
                  <div>CIP: {counts.applicable.cip.standards} standards · {counts.applicable.cip.reqs} reqs · {counts.applicable.cip.subs} subs</div>
                  <div>O&amp;P: {counts.applicable.op.standards} standards · {counts.applicable.op.reqs} reqs · {counts.applicable.op.subs} subs</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="count-applicable-empty">
            Set your <button className="underline" onClick={() => nav("/admin")}>Entity Profile</button> to see counts scoped to your functional registration.
          </p>
        )}
      </div>
    </div>
  );
}
